import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Users, UserMinus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StudentAssignment from '@/components/StudentAssignment';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * iter71.9 · Delete menu with two options:
 *   • «Para todos» → global delete (fires the existing `onDelete`
 *     callback provided by the parent tab, keeping current behaviour).
 *   • «Estudiantes específicos» → opens a modal with the shared
 *     `StudentAssignment` selector and calls
 *     `POST /content/{id}/unassign-students` so those alumnos stop
 *     seeing the content, but ALL their submissions / notes / audios
 *     / badges are preserved in the DB.
 *
 * Props:
 *   contentId       — id of the content to delete/unassign
 *   classStudents   — [{ id|user_id, first_name, last_name, … }]
 *   token           — auth token
 *   onDelete        — () => void  fires after global delete
 *   onUnassigned    — () => void  fires after selective unassignment (refresh)
 *   testIdPrefix    — string used to build data-testid values
 */
export default function DeleteContentMenu({
  contentId, classStudents = [], token,
  onDelete, onUnassigned, testIdPrefix = 'delete',
}) {
  const [openMenu, setOpenMenu] = useState(false);
  const [showUnassign, setShowUnassign] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!openMenu) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openMenu]);

  const handleGlobalDelete = () => {
    setOpenMenu(false);
    if (typeof onDelete === 'function') onDelete(contentId);
  };

  const openUnassignModal = () => {
    setOpenMenu(false);
    setSelectedStudents([]);
    setShowUnassign(true);
  };

  const handleUnassignConfirm = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/content/${contentId}/unassign-students`,
        { student_ids: selectedStudents },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message || 'Asignación retirada');
      setShowUnassign(false);
      setSelectedStudents([]);
      if (typeof onUnassigned === 'function') onUnassigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al retirar la asignación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpenMenu((v) => !v)}
        className="text-slate-300 hover:text-red-500 transition-colors"
        title="Eliminar"
        data-testid={`${testIdPrefix}-${contentId}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>

      {openMenu && (
        <div
          className="absolute right-0 top-6 z-40 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-1"
          data-testid={`${testIdPrefix}-menu-${contentId}`}
        >
          <button
            onClick={handleGlobalDelete}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
            data-testid={`${testIdPrefix}-all-${contentId}`}
          >
            <Users className="w-4 h-4" />
            Para todos
          </button>
          <button
            onClick={openUnassignModal}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-2"
            data-testid={`${testIdPrefix}-selective-${contentId}`}
          >
            <UserMinus className="w-4 h-4" />
            Estudiantes específicos
          </button>
        </div>
      )}

      {showUnassign && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
          data-testid={`${testIdPrefix}-unassign-modal-${contentId}`}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700">Retirar asignación</h3>
              <button
                onClick={() => setShowUnassign(false)}
                className="text-slate-400 hover:text-slate-600"
                data-testid={`${testIdPrefix}-unassign-close-${contentId}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <p className="text-xs text-slate-500 mb-3 leading-snug">
                Los alumnos seleccionados dejarán de ver este contenido, pero
                <b> sus entregas, notas y audios se conservan</b> en la base
                de datos.
              </p>
              <StudentAssignment
                students={classStudents}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                label="Retirar acceso a"
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-slate-50">
              <Button
                variant="outline"
                onClick={() => setShowUnassign(false)}
                disabled={submitting}
                data-testid={`${testIdPrefix}-unassign-cancel-${contentId}`}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUnassignConfirm}
                disabled={submitting || selectedStudents.length === 0}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid={`${testIdPrefix}-unassign-confirm-${contentId}`}
              >
                {submitting ? 'Retirando…' : `Retirar (${selectedStudents.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
